import type { ApolloClient, MutationUpdaterFn } from '@apollo/client';

import { getDataFromCache, updateCache } from 'src/modules/apollo/apolloCache';
import * as Schema from 'src/types/graphql';

import { getCandidateIdFromCache } from '../data';

export const updateCacheSetDefaultResume: MutationUpdaterFn<
  Schema.SetDefaultResumeMutation
> = (cache, { data }) => {
  const { setDefaultResume } = data || {};

  if (setDefaultResume?.viewer?.resumes) {
    const { resumes } = setDefaultResume.viewer;
    updateCache({ cache, data: resumes, fieldName: 'resumes' });
  }
};

export const updateCacheDeleteResume: MutationUpdaterFn<
  Schema.DeleteResumeMutation
> = (cache, { data }) => {
  const { deleteResume } = data || {};

  if (deleteResume?.viewer?.resumes) {
    const { resumes } = deleteResume.viewer;
    updateCache({ cache, data: resumes, fieldName: 'resumes' });
  }
};

export const generateSetDefaultResumeOptimisticResponse =
  ({ apolloClient }: { apolloClient: ApolloClient<any> }) =>
  ({ resumeId, languageCode }: Schema.SetDefaultResumeMutationVariables) => {
    const resumesCachedData = getDataFromCache({
      cache: apolloClient.cache,
      query: Schema.GetResumesDocument,
      variables: {
        languageCode,
      },
    });
    const _id = getCandidateIdFromCache(apolloClient.cache) || '';
    let defaultResumeIndex = null;
    let resumesOptimisticResponse =
      resumesCachedData?.viewer?.resumes?.map((resume, index) => {
        if (resume.id === resumeId.id) {
          defaultResumeIndex = index;
        }
        const isDefault = resume.id === resumeId.id;
        return { ...resume, isDefault };
      }) || [];

    if (defaultResumeIndex !== null) {
      const defaultResume = resumesOptimisticResponse[defaultResumeIndex];
      const nonDefaultResumes = resumesOptimisticResponse.filter(
        (resume) => !resume.isDefault,
      );
      resumesOptimisticResponse = [defaultResume, ...nonDefaultResumes];
    }
    return {
      __typename: 'Mutation',
      setDefaultResume: {
        __typename: 'SetDefaultResumePayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          resumes: [...resumesOptimisticResponse],
        },
      },
    };
  };

export const generateDeleteResumeOptimisticResponse =
  ({ apolloClient }: { apolloClient: ApolloClient<any> }) =>
  ({ resumeId, languageCode }: Schema.DeleteResumeMutationVariables) => {
    const resumesCachedData = getDataFromCache({
      cache: apolloClient.cache,
      query: Schema.GetResumesDocument,
      variables: {
        languageCode,
      },
    });
    const _id = getCandidateIdFromCache(apolloClient.cache) || '';
    const resumesOptimisticResponse =
      resumesCachedData?.viewer?.resumes?.filter(
        (resume) => resume.id !== resumeId.id,
      ) || [];
    return {
      __typename: 'Mutation',
      deleteResume: {
        __typename: 'DeleteResumePayload',
        ref: resumeId.id,
        viewer: {
          __typename: 'Candidate',
          _id,
          resumes: [...resumesOptimisticResponse],
        },
      },
    };
  };
